import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ChevronLeft, ChevronRight, Sun, Moon, Trash2, Plus, Calendar
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * TransferDatePicker - Calendrier interactif pour sélectionner plusieurs dates de transferts
 * 
 * Props:
 * - selectedDates: Array of { date: "YYYY-MM-DD", shift: "day"|"night" }
 * - onDatesChange: (dates) => void
 * - minDate: string (YYYY-MM-DD) - date minimum sélectionnable
 * - maxTransfers: number - nombre max de transferts
 */
const TransferDatePicker = ({ 
  selectedDates = [], 
  onDatesChange,
  minDate,
  maxTransfers = 20
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  // Générer les jours du mois
  const calendarDays = useMemo(() => {
    const { year, month } = currentMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    // Ajuster pour commencer le lundi (1 = Monday)
    const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    const days = [];
    
    // Jours du mois précédent
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = adjustedStartDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i)
      });
    }
    
    // Jours du mois actuel
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }
    
    // Jours du mois suivant (compléter jusqu'à 42 jours = 6 semaines)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }
    
    return days;
  }, [currentMonth]);

  // Formater une date en YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Vérifier si une date est sélectionnée
  const getDateSelections = (dateStr) => {
    return selectedDates.filter(s => s.date === dateStr);
  };

  // Vérifier si une date est dans le passé
  const isPastDate = (date) => {
    if (!minDate) return false;
    const minDateObj = new Date(minDate);
    minDateObj.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < minDateObj;
  };

  // Ajouter/supprimer un transfert pour une date
  const toggleDateShift = (dateStr, shift) => {
    const existing = selectedDates.find(s => s.date === dateStr && s.shift === shift);
    
    if (existing) {
      // Supprimer
      onDatesChange(selectedDates.filter(s => !(s.date === dateStr && s.shift === shift)));
    } else {
      // Ajouter (si pas au max)
      if (selectedDates.length < maxTransfers) {
        onDatesChange([...selectedDates, { date: dateStr, shift }]);
      }
    }
  };

  // Navigation du mois
  const goToPrevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { ...prev, month: prev.month + 1 };
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth({ year: today.getFullYear(), month: today.getMonth() });
  };

  // Supprimer un transfert spécifique
  const removeTransfer = (index) => {
    const newDates = [...selectedDates];
    newDates.splice(index, 1);
    onDatesChange(newDates);
  };

  // Noms des mois en français
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Noms des jours en français
  const dayNames = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

  // Formater une date pour l'affichage
  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="space-y-4" data-testid="transfer-date-picker">
      {/* En-tête du calendrier */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevMonth} data-testid="calendar-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-lg min-w-[160px] text-center" data-testid="calendar-month-label">
            {monthNames[currentMonth.month]} {currentMonth.year}
          </span>
          <Button variant="outline" size="icon" onClick={goToNextMonth} data-testid="calendar-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="calendar-today-btn">
            Aujourd'hui
          </Button>
          <Badge variant="outline" className="text-sm" data-testid="transfer-count-badge">
            {selectedDates.length}/{maxTransfers} transferts
          </Badge>
        </div>
      </div>

      {/* Grille du calendrier */}
      <div className="border rounded-lg overflow-hidden">
        {/* En-têtes des jours */}
        <div className="grid grid-cols-7 bg-gray-50 border-b">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
              {day}
            </div>
          ))}
        </div>

        {/* Jours du calendrier */}
        <div className="grid grid-cols-7">
          {calendarDays.map((dayInfo, index) => {
            const dateStr = formatDate(dayInfo.date);
            const selections = getDateSelections(dateStr);
            const hasDay = selections.some(s => s.shift === 'day');
            const hasNight = selections.some(s => s.shift === 'night');
            const isPast = isPastDate(new Date(dayInfo.date));
            const isToday = formatDate(new Date()) === dateStr;

            return (
              <div
                key={index}
                className={cn(
                  "min-h-[70px] p-1 border-b border-r transition-colors",
                  !dayInfo.isCurrentMonth && "bg-gray-50 text-gray-400",
                  dayInfo.isCurrentMonth && "bg-white",
                  isToday && "bg-blue-50",
                  isPast && "bg-gray-100 opacity-50"
                )}
              >
                <div className={cn(
                  "text-sm font-medium mb-1",
                  isToday && "text-blue-600"
                )}>
                  {dayInfo.day}
                </div>
                
                {dayInfo.isCurrentMonth && !isPast && (
                  <div className="flex flex-col gap-1">
                    {/* Bouton Jour */}
                    <button
                      onClick={() => toggleDateShift(dateStr, 'day')}
                      className={cn(
                        "flex items-center justify-center gap-1 text-xs py-1 px-1 rounded transition-all",
                        hasDay 
                          ? "bg-amber-100 text-amber-800 hover:bg-amber-200" 
                          : "bg-gray-100 text-gray-500 hover:bg-amber-50 hover:text-amber-700"
                      )}
                      disabled={!hasDay && selectedDates.length >= maxTransfers}
                    >
                      <Sun className="w-3 h-3" />
                      <span className="hidden sm:inline">J</span>
                    </button>
                    
                    {/* Bouton Nuit */}
                    <button
                      onClick={() => toggleDateShift(dateStr, 'night')}
                      className={cn(
                        "flex items-center justify-center gap-1 text-xs py-1 px-1 rounded transition-all",
                        hasNight 
                          ? "bg-indigo-100 text-indigo-800 hover:bg-indigo-200" 
                          : "bg-gray-100 text-gray-500 hover:bg-indigo-50 hover:text-indigo-700"
                      )}
                      disabled={!hasNight && selectedDates.length >= maxTransfers}
                    >
                      <Moon className="w-3 h-3" />
                      <span className="hidden sm:inline">N</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-amber-100 rounded"></div>
          <span>Jour (6h-18h)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-indigo-100 rounded"></div>
          <span>Nuit (18h-6h)</span>
        </div>
      </div>

      {/* Liste des transferts sélectionnés */}
      {selectedDates.length > 0 && (
        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm text-gray-700">
              Transferts planifiés ({selectedDates.length})
            </span>
            {selectedDates.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onDatesChange([])}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
              >
                Tout effacer
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedDates
              .sort((a, b) => {
                if (a.date !== b.date) return a.date.localeCompare(b.date);
                return a.shift === 'day' ? -1 : 1;
              })
              .map((selection, index) => (
                <Badge 
                  key={`${selection.date}-${selection.shift}-${index}`}
                  variant="outline"
                  className={cn(
                    "flex items-center gap-1 py-1 px-2",
                    selection.shift === 'day' ? "bg-amber-50 border-amber-200" : "bg-indigo-50 border-indigo-200"
                  )}
                >
                  {selection.shift === 'day' ? (
                    <Sun className="w-3 h-3 text-amber-600" />
                  ) : (
                    <Moon className="w-3 h-3 text-indigo-600" />
                  )}
                  <span className="text-xs">{formatDisplayDate(selection.date)}</span>
                  <button 
                    onClick={() => removeTransfer(index)}
                    className="ml-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
          </div>
        </div>
      )}

      {/* Message si aucun transfert */}
      {selectedDates.length === 0 && (
        <div className="text-center py-4 text-gray-500 text-sm border rounded-lg bg-gray-50">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>Cliquez sur les dates du calendrier pour planifier vos transferts</p>
          <p className="text-xs text-gray-400 mt-1">Jour (J) ou Nuit (N) pour chaque date</p>
        </div>
      )}
    </div>
  );
};

export default TransferDatePicker;
